// Persistent cache (IndexedDB) for the traceability queries.
// BACKWARD data (where goods came from) never changes once it is recorded,
// so it is kept for 14 days. FORWARD data (where goods went) can still
// change, so it is kept for 1 day.
// Failures are silent: without IndexedDB the queries just go to the API.

const DB_NAME = 'pmb-trace-cache'
const STORE = 'entries'
const DAY = 24 * 60 * 60 * 1000

export const TRACE_CACHE_TTL = {
  backward: 14 * DAY,
  forward: DAY,
}

let dbPromise = null

const purgeExpired = (db) => {
  try {
    const store = db.transaction(STORE, 'readwrite').objectStore(STORE)
    store.openCursor().onsuccess = (e) => {
      const cursor = e.target.result
      if (!cursor) return
      if (cursor.value.expiresAt < Date.now()) cursor.delete()
      cursor.continue()
    }
  } catch {
    // ignore
  }
}

// Opening waits behind a pending deleteDatabase (e.g. clearing the cache
// while another tab still has it open). Never let that block the API call.
const OPEN_TIMEOUT_MS = 1500

const openDb = () => {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }
    let settled = false
    const done = (db) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (!db) dbPromise = null // try again on the next query
      resolve(db)
    }
    const timer = setTimeout(() => done(null), OPEN_TIMEOUT_MS)
    try {
      const req = indexedDB.open(DB_NAME, 1)
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE, { keyPath: 'key' })
      }
      req.onsuccess = () => {
        const db = req.result
        // Let another tab delete or upgrade the cache instead of blocking it
        db.onversionchange = () => {
          db.close()
          dbPromise = null
        }
        if (settled) {
          db.close()
          return
        }
        purgeExpired(db)
        done(db)
      }
      req.onerror = () => done(null)
      req.onblocked = () => done(null)
    } catch {
      done(null)
    }
  })
  return dbPromise
}

// Run one request on the store; resolves with its result, or null on failure
const withStore = async (mode, action) => {
  const db = await openDb()
  if (!db) return null
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode)
      const req = action(tx.objectStore(STORE))
      tx.oncomplete = () => resolve(req?.result ?? null)
      tx.onerror = () => resolve(null)
      tx.onabort = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

// Same request (url + params, any order) for the same user = same key
export const traceCacheKey = (userKey, url, params) => {
  const sorted = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null)
    .sort()
    .map((k) => `${k}=${params[k]}`)
    .join('&')
  return `${userKey}|${url}|${sorted}`
}

// maxAge: only accept an entry saved within this time (a caller that needs
// fresher data than the entry's own lifetime can ask for it)
export const readTraceCache = async (key, maxAge = Infinity) => {
  const entry = await withStore('readonly', (store) => store.get(key))
  if (!entry || entry.expiresAt < Date.now()) return null
  if (Date.now() - entry.savedAt > maxAge) return null
  return entry.data
}

export const writeTraceCache = (key, data, ttl, lotId) =>
  withStore('readwrite', (store) =>
    store.put({
      key,
      lotId: String(lotId ?? ''),
      data,
      savedAt: Date.now(),
      expiresAt: Date.now() + ttl,
    }),
  )

// Small, stable hash so the token itself is never stored in a key
export const hashString = (value) => {
  let hash = 5381
  const str = String(value ?? '')
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(36)
}
